import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import {
  NotificationsTableHeadCells,
  getComparator,
  stableSort,
  routes,
} from "../../../../constants";
import { NotificationTableRow } from "../components";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import CommonTableHead from "../../../../common/CommonTableHead";
import { useNotificationRowHighlight } from "../../../../hooks/useNotificationRowHighlight";

const mainBoxContainerStyles = {
  width: "100%",
  marginTop: 2,
};

const tablePaperContainerStyles = {
  width: "100%",
  mb: 2,
  boxShadow: "none",
  border: "1px solid #e0e0e0",
};

export default function NotificationsTable({
  notifications,
  hasMore,
  onLoadMore,
  loading,
  pageSize,
  onPageSizeChange,
}) {
  const navigate = useNavigate();
  const { isNotificationNew } = useNotificationRowHighlight();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  // Initialize column widths based on NotificationsTableHeadCells
  const initializeColumnWidths = () => {
    const widths = {};
    NotificationsTableHeadCells.forEach(cell => {
      widths[cell.id] = cell.width || 150;
    });
    return widths;
  };

  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - notifications.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(notifications, getComparator(order, orderBy)).slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, notifications]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    onPageSizeChange(newRowsPerPage);
  };

  const handleQuickTaleClick = (notification) => {
    if (notification.quicktaleData && notification.object) {
      // Narrator updates to batch QuickTales open the batch QuickTale details page.
      if (
        notification.type === "quicktale_update" &&
        notification.collection === "batchQuicktales"
      ) {
        const batchId = notification.batchId || notification.quicktaleData.batchId;
        if (batchId) {
          navigate(
            routes.batchQuicktaleDetails
              .replace(":batchId", batchId)
              .replace(":batchQuicktaleId", notification.object),
            {
              state: {
                quicktaleDetails: {
                  ...notification.quicktaleData,
                  id: notification.object,
                },
              },
            }
          );
          return;
        }
      }

      let routePath;
      
      // Different routing logic based on notification type
      if (notification.type === "quicktale_reaction") {
        // For reaction notifications, always go to QuickTales in Organization details
        routePath = routes.quicktaleInOrgDetails;
      } else {
        // For submission notifications, route based on status
        routePath = notification.status === "pending" 
          ? routes.addQuicktalesToOrgDetails 
          : routes.quicktaleInOrgDetails;
      }
      
      // Pass the complete quicktale data, ensuring the ID is set correctly
      const quicktaleDetails = {
        ...notification.quicktaleData,
        id: notification.object, // Ensure the ID matches the notification object
      };
      
      // Debug logging to see what data we're passing
      console.log("Navigating with quicktaleDetails:", quicktaleDetails);
      console.log("Notification type:", notification.type);
      console.log("Route path:", routePath);
      console.log("photoUrl:", quicktaleDetails.photoUrl);
      console.log("imageUrls:", quicktaleDetails.imageUrls);
      
      navigate(routePath, {
        state: { 
          quicktaleDetails: quicktaleDetails
        },
      });
    }
  };

  return (
    <Box sx={mainBoxContainerStyles}>
      <Paper sx={tablePaperContainerStyles}>
        <TableContainer
          style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
          >
            <CommonTableHead
              numSelected={0}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={() => {}}
              onRequestSort={handleRequestSort}
              rowCount={visibleRows.length}
              tableHeadCells={NotificationsTableHeadCells}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              hideCheckbox={true}
            />

            <TableBody>
              {visibleRows.map((notification, index) => {
                const labelId = `notifications-table-${index}`;
                const numberType = index % 2 === 0;

                return (
                  <NotificationTableRow
                    key={notification.id}
                    notification={notification}
                    labelId={labelId}
                    numberType={numberType}
                    columnWidths={columnWidths}
                    onQuickTaleClick={handleQuickTaleClick}
                    isNewNotification={isNotificationNew(notification)}
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={2} />
                </TableRow>
              )}
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No notifications found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <TablePaginationSection
        count={Math.ceil(notifications.length / rowsPerPage)}
        page={page}
        total={notifications.length ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
